import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ContentCopy from '@mui/icons-material/ContentCopy';
import EventIcon from '@mui/icons-material/EventOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiDrawer from '@mui/material/Drawer';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { enGB } from 'date-fns/locale';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetCreditBalanceQuery, useGetUserPublicKeyQuery } from '../../../../../../../../api/LabtraceApi';
import { DialogContext } from '../../../../../../../../hooks/DialogContext';
import { download } from '../../../../../../../../services/Utils/Utils';

const DrawerContentWrapper = styled(Box)(() => ({
    padding: '1.5rem 1rem',
    height: '100vh',
    position: 'relative'
}));

const StyledButton = styled(Button)(({ theme }) => ({
    color: theme.palette.primary.main,
    alignItems: 'center'
}));

const ButtonStyle = styled(Button)(() => ({
    width: '127px',
    height: '48px'
}));

const StyledDurationTextField = styled(TextField)(({ theme }) => ({
    width: '100%',
    '& .MuiOutlinedInput-root.Mui-focused svg': {
        color: theme.palette.primary.main
    }
}));

const CreditBalanceDrawer = ({ isOpen, anchor, onClose }) => {
    const [values, setValues] = useState({
        startEndDate: {
            value: [null, null]
        }
    });
    const user = useSelector((state) => state.user);
    const { openDialog } = useContext(DialogContext);
    const { data: publicKey } = useGetUserPublicKeyQuery(user.id, { skip: !user.id });
    const { data: creditData } = useGetCreditBalanceQuery(user.id, { skip: !user.id });
    const [isCopied, setIsCopied] = useState(false);

    const copyToClipboard = () => {
        navigator.clipboard.writeText(publicKey.pubKey);
        setIsCopied(true);
        setTimeout(() => {
            setIsCopied(false);
        }, 2000);
    };

    const handleAutocompleteChange = (name, index, value) => {
        setValues((prev) => {
            const updatedDates = [...(prev[name]?.value || [null, null])];
            updatedDates[index] = value;
            return {
                ...prev,
                [name]: {
                    ...prev[name],
                    value: updatedDates
                }
            };
        });
    };

    const downloadCreditBalanceHistory = async () => {
        await download({
            type: 'creditHistory',
            openDialog,
            userId: user.id,
            from: moment(values.startEndDate.value[0]).format('YYYY-MM-DD'),
            to: moment(values.startEndDate.value[1]).format('YYYY-MM-DD')
        });
    };

    const [startDate, endDate] = Array.isArray(values.startEndDate?.value)
        ? values.startEndDate.value
        : [null, null];

    return (
        <MuiDrawer open={isOpen} anchor={anchor} onClose={onClose}>
            <DrawerContentWrapper
                component="form"
                noValidate
                autoComplete="off"
            >
                <StyledButton variant="text" startIcon={<ChevronLeftIcon />} onClick={onClose}>
                    <Typography variant="button">Back</Typography>
                </StyledButton>
                <Grid container spacing={1} sx={{ mt: '2rem', mb: '3rem', width: '480px' }}>
                    <Grid item xs={7}>
                        <Typography variant="subtitle2">Available Credit Balance</Typography>
                        <Typography variant="h6" color="primary.main">
                            { creditData && creditData.creditBalance }
                        </Typography>
                    </Grid>
                    <Grid item xs={10} sx={{ mt: '30px' }}>
                        <Typography variant="subtitle2">Public Key</Typography>
                        <Typography variant="h6" color="primary.main" sx={{ display: 'flex' }}>
                            { publicKey && (`${publicKey.pubKey.substring(0, 6)} . . . ${publicKey.pubKey.substring(publicKey.pubKey.length - 4)}`)}
                            <Button
                                variant="outlined"
                                startIcon={<ContentCopy />}
                                sx={{ ml: '1rem' }}
                                onClick={copyToClipboard}
                            >
                                <Typography variant="button">
                                    {isCopied ? 'Copied!' : 'Copy'}
                                </Typography>
                            </Button>
                        </Typography>
                    </Grid>
                </Grid>
                <Typography variant="subtitle2" sx={{ mb: '1rem' }}>History of transactions</Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                    <DatePicker
                        label="Start date"
                        value={startDate}
                        onChange={(newValue) => handleAutocompleteChange('startEndDate', 0, newValue)}
                        renderInput={(params) => (
                            <StyledDurationTextField
                                {...params}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <EventIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />

                    <DatePicker
                        label="End date"
                        value={endDate}
                        minDate={startDate || undefined}
                        onChange={(newValue) => handleAutocompleteChange('startEndDate', 1, newValue)}
                        renderInput={(params) => (
                            <StyledDurationTextField
                                {...params}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <EventIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />

                </LocalizationProvider>
                <StyledButton
                    variant="text"
                    size="large"
                    sx={{ mt: '2.5rem', width: '100%' }}
                    disabled={!values.startEndDate.value[0] || !values.startEndDate.value[1]}
                    onClick={downloadCreditBalanceHistory}
                >
                    <Typography variant="button">Download history</Typography>
                </StyledButton>
            </DrawerContentWrapper>
        </MuiDrawer>
    );
};

CreditBalanceDrawer.defaultProps = {
    anchor: 'right'
};

CreditBalanceDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    anchor: PropTypes.string,
    onClose: PropTypes.func.isRequired
};

export default CreditBalanceDrawer;
